import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { IconDownload, IconEdit, IconTrash } from "@tabler/icons-react"
import { type FC } from "react"

import { type Downloadable as DownloadableType } from "@forento/core/models/downloadable"
import { getExtensionByFileName } from "@forento/core/utilities/file"
import { toNumberFormat } from "@forento/core/utilities/number"
import { toSlug } from "@forento/core/utilities/string"

import ProductListRow from "~/components/ProductListRow"
import { useAlert } from "~/contexts/AlertContext"
import { downloadBlob } from "~/utilities/element"
import routes from "~/utilities/routes"
import trpc from "~/utilities/trpc"

type Props = { downloadable: DownloadableType; onDelete(): void }
const Downloadable: FC<Props> = ({ downloadable, onDelete }) => {
	const alert = useAlert()
	const { setNodeRef, transform, transition, attributes, listeners } = useSortable({ id: downloadable.id })

	const extension = downloadable.type === "file" ? getExtensionByFileName(downloadable.filePath) : null

	const handleDownload = async () => {
		if (downloadable.type === "url") {
			window.open(downloadable.url, "_blank", "noreferrer noopener")
			return
		}
		const response = await fetch(downloadable.filePath, { headers: { "Content-Type": "application/octet-stream" } })
		downloadBlob(await response.blob(), toSlug(downloadable.name) + (extension !== null ? `.${extension}` : ""))
	}

	const hasOrders = downloadable.orderCount > 0

	const handleDelete = async () => {
		const dialog = await alert.confirm(
			"Delete downloadable",
			`Are you sure you want to delete the downloadable *${downloadable.name}*?` +
				(hasOrders
					? `\n\n**Warning:** it has ${toNumberFormat(downloadable.orderCount)} ${downloadable.orderCount === 1 ? "purchase" : "purchases"}. Deleting it removes the buyers' access to it.`
					: ""),
		)
		if (!dialog.result) return

		await trpc.downloadable.delete.mutate(downloadable.id)

		dialog.close()
		onDelete()
	}

	return (
		<ProductListRow
			ref={setNodeRef}
			title={downloadable.name}
			sticker={extension ?? (downloadable.type === "url" ? "URL" : undefined)}
			subtitle={
				downloadable.price !== null
					? `${toNumberFormat(downloadable.orderCount)} ${downloadable.orderCount === 1 ? "purchase" : "purchases"}`
					: undefined
			}
			actions={[
				{ id: "download", tooltip: "Download", icon: IconDownload, onClick: handleDownload },
				{ id: "edit", tooltip: "Edit", icon: IconEdit, href: routes.downloadable.edit(downloadable.id) },
				{ id: "delete", tooltip: "Delete", icon: IconTrash, danger: true, onClick: handleDelete },
			]}
			dragHandle={{ attributes, listeners }}
			style={{ transform: CSS.Transform.toString(transform), transition }}
		/>
	)
}

export default Downloadable
